import React from 'react';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import { Lightbox } from "react-modal-image";
import MuiAlert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import SimpleTable from '../../components/SimpleTable';
import firebase from '../../utilities/firebase';
import history from '../../utilities/history';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { Delete } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import AddInvoiceDialog from '../../components/AddInvoiceDialog';
import { StateManager } from '../../utilities/stateManager';

export default function Invoices(props) {
  const { stockNumber, deal = {}, invoices = [], type = "sales", onInvoiceSelectionChange } = props;
  const [open, setOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingInvoice, setEditingInvoice] = React.useState(null);
  const [localInvoices, setLocalInvoices] = React.useState(invoices);
  const [deleteSnackbar, setDeleteSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [pendingInvoiceSelection, setPendingInvoiceSelection] = React.useState(null);
  const params = settings[type];
  
  // Update local invoices when props change
  React.useEffect(() => {
    setLocalInvoices(invoices);
  }, [invoices]);
  
    let initChecked = {};
  localInvoices.forEach(doc => initChecked[doc.id] = doc.id === deal[params.key]);
  const [checked, setChecked] = React.useState(initChecked);

  const trades = deal.trades || [];
  const [tradeValue, setTradeValue] = React.useState(0);

  React.useEffect(() => {
    async function fetchData() {
      const tradeValue = await getTaxDeduction(trades);
      setTradeValue(tradeValue);
    }
    fetchData();
  }, []);

  const initValue = localInvoices.filter(doc => doc.id ==  deal[params.key])
                        .map(doc => doc.total)
                        .reduce((a,c) => a + c, 0);
  const [value, setValue] = React.useState(initValue);
  const [bigChecked, setBigChecked] = React.useState(value > 0);
  
  React.useLayoutEffect(() => {
      setBigChecked(value > 0)
  }, [value]);



  // Handle pending invoice selection after state updates
  React.useEffect(() => {
    if (pendingInvoiceSelection && localInvoices.length > 0) {
      const invoiceExists = localInvoices.find(inv => inv.id === pendingInvoiceSelection);
      if (invoiceExists) {
        // Call selectRow with the current localInvoices data
        handleSelectRow(pendingInvoiceSelection);
        setPendingInvoiceSelection(null);
      }
    }
  }, [localInvoices, pendingInvoiceSelection]);

  // Helper function to handle row selection with current data
  const handleSelectRow = async (id) => {
    if(stockNumber) await firebase.firestore().doc('deals/'+stockNumber).set({[params.key]: id}, {merge:true});

    let newChecked = {}
    localInvoices.forEach(doc => newChecked[doc.id] = doc.id === id);
    setChecked(newChecked);

    const newValue = localInvoices.filter(doc => doc.id == id)
                              .map(doc => doc.total)
                              .reduce((a,c) => a + c, 0);
    setValue(newValue);
    
    // Notify parent component about the invoice selection change
    if (onInvoiceSelectionChange) {
      const newActiveInvoice = localInvoices.filter(doc => doc.id == id)[0];
      onInvoiceSelectionChange(newActiveInvoice);
    }
  };

  const clickCheckbox = (e, id) => {
    if(props.disabled) return;
    e.stopPropagation();
    handleSelectRow(id);
  }

  const Selector = (id) => (
    <Checkbox checked={checked[id]} onClick={e => clickCheckbox(e,id)} disabled={props.disabled} />
  );

  const makeInvoice = () => {
    setOpen(true);
  };

  const editInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setEditOpen(true);
  };

  const deleteInvoice = async (invoiceId, e) => {
    e.stopPropagation();
    
    if (!StateManager.isAdmin()) {
      return;
    }

    try {
      // Delete the invoice from Firebase
      await firebase.firestore().collection('invoices').doc(invoiceId).delete();
      
      // Remove from local state
      const updatedInvoices = localInvoices.filter(inv => inv.id !== invoiceId);
      setLocalInvoices(updatedInvoices);
      
      // If the deleted invoice was selected, clear the selection
      if (checked[invoiceId]) {
        const newChecked = {};
        updatedInvoices.forEach(doc => newChecked[doc.id] = false);
        setChecked(newChecked);
        setValue(0);
        setBigChecked(false);
        
        // Clear the deal's invoice reference if it was this one
        if (stockNumber && deal[params.key] === invoiceId) {
          await firebase.firestore().doc('deals/'+stockNumber).set({[params.key]: null}, {merge:true});
        }
      }
      
      setDeleteSnackbar({
        open: true,
        message: 'Invoice deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting invoice:', error);
      setDeleteSnackbar({
        open: true,
        message: 'Error deleting invoice',
        severity: 'error'
      });
    }
  };

  const handleInvoiceAdded = async (invoiceData) => {
    console.log('handleInvoiceAdded called with:', invoiceData);
    try {
      // Refresh the invoices list by fetching from Firebase
      const invoicesRef = firebase.firestore().collection('invoices');
      const query = invoicesRef.where('stock', '==', stockNumber);
      if(type === 'finance') query.where('type', '==', type);
      const snapshot = await query.get();
      
      const newInvoices = [];
      snapshot.forEach(doc => {
        newInvoices.push({ id: doc.id, ...doc.data() });
      });
      
      console.log('Fetched new invoices:', newInvoices);
      
      // Update local state with new invoices
      setLocalInvoices(newInvoices);
      
      // Set pending selection - will be handled by useEffect when state updates
      setPendingInvoiceSelection(invoiceData.id);
      
      // Close the dialog
      setOpen(false);
    } catch (error) {
      console.error('Error handling new invoice:', error);
    }
  };

  const handleInvoiceEdited = async (invoiceData) => {
    console.log('handleInvoiceEdited called with:', invoiceData);
    try {
      // Refresh the invoices list by fetching from Firebase
      const invoicesRef = firebase.firestore().collection('invoices');
      const query = invoicesRef.where('stock', '==', stockNumber);
      if(type === 'finance') query.where('type', '==', type);
      const snapshot = await query.get();
      
      const newInvoices = [];
      snapshot.forEach(doc => {
        newInvoices.push({ id: doc.id, ...doc.data() });
      });
      
      console.log('Fetched updated invoices:', newInvoices);
      
      // Update local state with new invoices
      setLocalInvoices(newInvoices);
      
      // Set pending selection - will be handled by useEffect when state updates
      setPendingInvoiceSelection(invoiceData.id);
      
      // Close the edit dialog
      setEditOpen(false);
      setEditingInvoice(null);
    } catch (error) {
      console.error('Error handling edited invoice:', error);
    }
  };

  const rows = localInvoices.map( 
    doc => {
      const variables = Object.keys(doc)
        .filter(key => key !== 'total')
        .map(key => `${key}=${doc[key]}`).join("&");
      // console.log(variables)
      const tab = new URL(window.location.href).searchParams.get("tab");
      const redirect = `../car/${stockNumber}`;
      return {
        id: doc.id, 
        ...doc,
        taxes: (parseFloat(doc.salesTax) || 0) + (parseFloat(doc.surtax) || 0),
        actions: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {Selector(doc.id)}
            {StateManager.isAdmin() && (
              <IconButton
                size="small"
                color="error"
                onClick={(e) => deleteInvoice(doc.id, e)}
                title="Delete Invoice"
                disabled={props.disabled}
              >
                <Delete />
              </IconButton>
            )}
          </div>
        ),
        rowAction: props.disabled ? null : () => editInvoice(doc),
        // rowLink: props.disabled ? null : `../form/${params.form}?i=${doc.id}&${variables}&tab=${tab}&redirect=${redirect}`,
        // rowAction: () => showFile(doc.data().files[0])
      }
    }
  );

  const tableData = {
    rows,
    headers: params.headers,
    title: props.title, 
  };

  const handleCloseSnackbar = () => {
    setDeleteSnackbar({ ...deleteSnackbar, open: false });
  };

  return (
    <Accordion>
      <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
      >
        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
          <FormControlLabel control={<Checkbox checked={bigChecked} onClick={e => e.stopPropagation()} />} label={params.label} />
          <Typography variant='body1' style={{display: 'flex', alignItems: "center"}}>
            ${(value || 0).toLocaleString(undefined, {minimumFractionDigits:2})}
          </Typography>
        </div>
      
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SimpleTable {...tableData} disabled={props.disabled}/>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" color="primary" onClick={makeInvoice} disabled={props.disabled}>
              Make New Invoice
            </Button>
          </Grid>
        </Grid>
      </AccordionDetails>
      <AddInvoiceDialog
        open={open}
        onClose={() => setOpen(false)}
        stockNumber={stockNumber}
        type={type}
        tax_deduction={tradeValue}
        onInvoiceAdded={handleInvoiceAdded}
      />
      <AddInvoiceDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditingInvoice(null);
        }}
        stockNumber={stockNumber}
        type={type}
        tax_deduction={tradeValue}
        onInvoiceAdded={handleInvoiceEdited}
        editMode={true}
        invoiceData={editingInvoice}
      />
      <Snackbar
        open={deleteSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar}
          severity={deleteSnackbar.severity}
        >
          {deleteSnackbar.message}
        </MuiAlert>
      </Snackbar>
    </Accordion>
  );
}

const settings = {
  sales:{
    label: "Invoices",
    form: "deal",
    key: "invoice",
    headers: [{key:'actions', label:'Actions'}, {key:'cashPrice', label:'Price', format:'usd'}, {key:'docFee', label:'Doc Fee', format:'usd'}, {key:'titleFee', label:'Title Fee', format:'usd'}, {key:'tagFee', label:'Tag Fee', format:'usd'},  {key:'conciergeFee', label:'Concierge Fee', format:'usd'}, {key:'taxes', label:'Taxes', format:'usd'}, {key:'total', label:'Total Amount', format:'usd'}],
  },
  shipping:{
    label: "Shipping Invoices",
    form: "shipping",
    key: "shipping",
    headers: [{key:'actions', label:'Actions'}, {key:'origin', label:'Origin Zip'}, {key:'destination', label:'Destination Zip'}, {key:'price', label:'Price', format:'usd'}, {key:'adjustments', label:'Adjustments', format:'usd'}, {key:'total', label:'Total Amount', format:'usd'}],
  },
  shipping_in:{
    label: "Shipping To Skyway Invoices",
    form: "shipping_in",
    key: "shipping_in",
    headers: [{key:'actions', label:'Actions'}, {key:'price', label:'Price', format:'usd'}, {key:'adjustments', label:'Adjustments', format:'usd'}, {key:'total', label:'Total Amount', format:'usd'}],
  },
  finance:{
    label: "Finance Invoices",
    form: "finance",
    key: "finance",
    headers: [{key:'actions', label:'Actions'}, {key:'conciergeFee', label:'Concierge Fee', format:'usd'}, {key:'participationFee', label:'Participation Fee', format:'usd'}, {key:'adjustments', label:'Adjustments', format:'usd'}, {key:'total', label:'Total Amount', format:'usd'}],
  }
}



const getTaxDeduction = async (tradeIDs) => {
  const db = firebase.firestore();
  const docs = tradeIDs.map(id => db.doc('trades/'+id).get());
  const tradeDocs = await Promise.all(docs);
  const totalTradeValue = tradeDocs.map(doc => (doc.data() || {}).trade).reduce((a,c) => a + c, 0);

  return totalTradeValue;
}
